// Deterministic, SSR-safe shuffle / sample.
//
// Why this exists: never use Math.random / Date.now to randomise content in a
// component's render output. They produce a different result during the Gatsby
// build (server HTML) than during client hydration, and under React 17 the
// mismatched <a href> / <img src> attributes are NOT reconciled — leaving cards
// wired to the wrong post. Deriving all randomness from a stable seed string
// makes the server and client agree, so the order is "random-looking but stable":
// it varies per input set yet is identical on every visit/refresh.

// FNV-1a: stable string -> 32-bit unsigned seed.
const stringToSeed = (str) => {
  let h = 2166136261 >>> 0
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

// mulberry32: small, fast PRNG that is fully deterministic for a given seed.
const mulberry32 = (seed) => {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// Returns a new array deterministically shuffled by seedString (input not mutated).
export const seededShuffle = (array, seedString = '') => {
  const rand = mulberry32(stringToSeed(String(seedString)))
  const result = [...array]
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1))
    const tmp = result[i]
    result[i] = result[j]
    result[j] = tmp
  }
  return result
}

// Deterministically pick `count` items from `array`, seeded by seedString.
export const seededSample = (array, count, seedString = '') =>
  seededShuffle(array, seedString).slice(0, count)
